/**
 * Labels, colours and helpers for the validation run's stage rail.
 *
 * The rail is the one piece of vocabulary every screen in this feature
 * shares, so it lives in one place: the list page badge, the detail page
 * stepper, and the tester's "run status" chip all read these.
 */

import type { Readiness, RunStatus, TestStatus } from "@/shared/api/validation";

export interface Stage {
  key: RunStatus;
  /** Short label for the stepper. */
  label: string;
  /** What the stage means, for the stepper description. */
  hint: string;
  /** Which tab of the detail page shows this stage's work. */
  tab: "risk" | "plan" | "tests" | "execution" | "report" | "approval";
}

/** Ordered rail. `rejected` is off the rail (a side exit from `reported`). */
export const STAGES: Stage[] = [
  { key: "draft", label: "Risk assessment", hint: "Attach the release comparison", tab: "risk" },
  { key: "assessed", label: "Confirm risks", hint: "Human gate 1", tab: "risk" },
  { key: "risks_confirmed", label: "Plan", hint: "AI drafts the validation plan", tab: "plan" },
  { key: "planned", label: "Tests", hint: "AI authors and classifies tests", tab: "tests" },
  { key: "authored", label: "Approve & assign", hint: "Human gate 2", tab: "tests" },
  {
    key: "executing",
    label: "Execute",
    hint: "Automatic via Veeva API · manual by testers",
    tab: "execution",
  },
  { key: "reported", label: "Report", hint: "Coverage, defects, readiness", tab: "report" },
  { key: "approved", label: "Sign-off", hint: "Human gate 3 — e-signature", tab: "approval" },
];

export const STATUS_LABEL: Record<RunStatus, string> = {
  draft: "Draft",
  assessed: "Risks to confirm",
  risks_confirmed: "Planning",
  planned: "Plan ready",
  authored: "Tests drafted",
  executing: "Executing",
  reported: "Awaiting sign-off",
  approved: "Approved",
  rejected: "Rejected",
};

export const STATUS_COLOR: Record<RunStatus, string> = {
  draft: "gray",
  assessed: "yellow",
  risks_confirmed: "blue",
  planned: "blue",
  authored: "yellow",
  executing: "blue",
  reported: "yellow",
  approved: "green",
  rejected: "red",
};

export const TEST_STATUS_COLOR: Record<TestStatus, string> = {
  draft: "gray",
  approved: "blue",
  assigned: "blue",
  in_progress: "yellow",
  passed: "green",
  failed: "red",
  blocked: "orange",
};

export const TEST_STATUS_LABEL: Record<TestStatus, string> = {
  draft: "Draft",
  approved: "Ready",
  assigned: "Assigned",
  in_progress: "In progress",
  passed: "Passed",
  failed: "Failed",
  blocked: "Blocked",
};

export const READINESS_LABEL: Record<Readiness, string> = {
  go: "GO",
  go_with_conditions: "GO WITH CONDITIONS",
  no_go: "NO-GO",
};

export const READINESS_COLOR: Record<Readiness, string> = {
  go: "green",
  go_with_conditions: "yellow",
  no_go: "red",
};

/** GxP risk word → colour, matching the risk report's own ramp. */
export function riskColor(rating: string | null | undefined): string {
  switch ((rating ?? "").toLowerCase()) {
    case "high":
      return "red";
    case "medium":
      return "yellow";
    case "low":
      return "gray";
    default:
      return "gray";
  }
}

/** Index on the rail, or -1 for rejected. */
export function stageIndex(status: RunStatus): number {
  return STAGES.findIndex((s) => s.key === status);
}

/** Which tab the run is "at" — where the next human action lives. */
export function activeTab(status: RunStatus): Stage["tab"] {
  if (status === "rejected") return "report";
  return STAGES[stageIndex(status)]?.tab ?? "risk";
}

export function fmtDate(value: string | null | undefined): string {
  if (!value) return "—";
  return new Date(value).toLocaleString();
}
